import { Capacitor } from '@capacitor/core';
import {
  AppTrackingTransparency,
  type AppTrackingStatus,
} from 'capacitor-plugin-app-tracking-transparency';
import { getDeviceOsName, getDeviceOsVersion } from '@bridebook/web/lib/app/selectors';
import { DevicePlatform, IApplicationState } from '@bridebook/web/lib/types';

export interface MetaCapiMetadata {
  advertiserTrackingStatus?: AppTrackingStatus;
  os: {
    name: string;
    version: string;
  };
}

/**
 * Helper for getting the App Tracking Transparency (ATT) status. Only available in the native iOS app.
 */
const getAdvertiserTrackingStatus = async (): Promise<AppTrackingStatus | undefined> => {
  const nativePlatform = Capacitor.getPlatform();

  if (
    nativePlatform !== DevicePlatform.IOS ||
    !Capacitor.isPluginAvailable('AppTrackingTransparency')
  ) {
    return undefined;
  }

  try {
    const appTrackingStatus = await AppTrackingTransparency.getStatus();
    return appTrackingStatus?.status;
  } catch {
    return undefined;
  }
};

/**
 * Gets the Meta CAPI (Conversion API) metadata for the current user.
 * - App Tracking Transparency (ATT) status. Only available in the native iOS app.
 * - Operating system name and version, read from Redux state (populated at app startup).
 */
export const getMetaCapiMetadata = async (state: IApplicationState): Promise<MetaCapiMetadata> => {
  const os = {
    name: getDeviceOsName(state),
    version: getDeviceOsVersion(state),
  };

  if (!Capacitor.isNativePlatform()) {
    return { os };
  }

  const advertiserTrackingStatus = await getAdvertiserTrackingStatus();

  return {
    os,
    ...(advertiserTrackingStatus && { advertiserTrackingStatus }),
  };
};
